<template>
  <Breadcrumbs
      title="View Module"
      :breadcrumbs="[
      { label: 'Master Data', path: '' },
      { label: 'Module & Route Configurations', path: '/module-route-configurations' },
      { label: 'View Module', path: '' },
    ]"
  />

  <div class="container-fluid">
    <div class="row project-cards">
      <div class="col-sm-12">
        <div class="card">
          <div class="card-body">
            <!-- Loading Indicator -->
            <div v-if="loading" class="d-flex flex-column align-items-center justify-content-center py-5">
              <div class="spinner-border" style="color: #003366; width: 3rem; height: 3rem;" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-3 text-muted" style="font-size: 1rem;">Fetching module details...</p>
            </div>

            <template v-else>
              <div class="row mb-4">
                <div class="col-12 form-group">
                  <label class="text-muted">Module Name</label>
                  <h5 class="font-weight-bold mb-0 text-uppercase" style="color: #333;">{{ moduleName }}</h5>
                </div>
              </div>

              <div class="d-flex justify-content-between align-items-center mb-3">
                <h6 class="mb-0 font-weight-bold">Submodules</h6>
              </div>

              <div v-if="validSubmodules.length > 0" class="position-relative mb-4 pb-2">
                <button v-if="showLeftArrow" @click="scrollLeft" class="btn btn-light shadow position-absolute" style="top: 50%; left: -15px; transform: translateY(-50%); z-index: 10; width: 35px; height: 35px; border-radius: 50%; padding: 0; display: flex; align-items: center; justify-content: center;">
                  <i class="fa fa-chevron-left text-primary" style="margin-left: -2px;"></i>
                </button>

                <div ref="scrollContainer" @scroll="handleScroll" class="d-flex flex-nowrap overflow-auto hide-scrollbar py-2 px-1" style="gap: 15px;">
                  <div 
                    v-for="submodule in validSubmodules" 
                    :key="submodule.submodule_id" 
                    class="card mb-0 shadow-sm" 
                    style="min-width: 220px; flex: 0 0 auto; border: 1px solid #e0e0e0; border-radius: 8px;"
                  >
                    <div class="card-body d-flex flex-column justify-content-between p-3">
                      <h6 class="font-weight-bold text-secondary mb-3 text-truncate" :title="submodule.submodule_name">
                        <i class="fa fa-folder-open me-2"></i> {{ submodule.submodule_name }}
                      </h6>
                      <div class="d-flex justify-content-between align-items-center mt-auto">
                        <span class="text-muted small">
                          <strong>{{ submodule.routes ? submodule.routes.length : 0 }}</strong> Route(s)
                        </span>
                        <span class="badge px-3 py-2" :style="{ backgroundColor: submodule.is_active ? '#198754' : '#dc3545', color: '#fff', borderRadius: '12px' }">
                          {{ submodule.is_active ? 'Active' : 'Inactive' }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <button v-if="showRightArrow" @click="scrollRight" class="btn btn-light shadow position-absolute" style="top: 50%; right: -15px; transform: translateY(-50%); z-index: 10; width: 35px; height: 35px; border-radius: 50%; padding: 0; display: flex; align-items: center; justify-content: center;">
                  <i class="fa fa-chevron-right text-primary" style="margin-right: -2px;"></i>
                </button>
              </div>
              
              <div v-else class="text-center text-muted py-5 mb-4 border rounded bg-light d-flex flex-column align-items-center justify-content-center">
                <i class="fa fa-folder-open-o mb-3" style="font-size: 48px; color: #ced4da;"></i>
                <h6 class="font-weight-bold mb-1">No Submodules</h6>
                <p class="mb-0 small">This module doesn't have any submodules configured.</p>
              </div>

              <div class="d-flex justify-content-between align-items-center mb-3 mt-4">
                <h6 class="mb-0 font-weight-bold">Routes</h6>
              </div>

              <div v-if="allRoutes.length > 0" class="table-responsive">
                <table class="table table-bordered table-hover">
                  <thead class="table-light">
                    <tr>
                      <th>Name</th>
                      <th>Submodule</th>
                      <th>Page Type</th>
                      <th>URI</th>
                      <th class="text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="route in allRoutes" :key="route.route_id">
                      <td class="align-middle">{{ route.route_name }}</td>
                      <td class="align-middle">{{ route.submodule_name }}</td>
                      <td class="align-middle">{{ route.page_type?.name || '—' }}</td>
                      <td class="align-middle text-lowercase">{{ route.uri }}</td>
                      <td class="text-center align-middle">
                        <span class="badge px-3 py-2" :style="{ backgroundColor: route.is_active ? '#198754' : '#dc3545', color: '#fff', borderRadius: '12px' }">
                          {{ route.is_active ? 'Active' : 'Inactive' }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div v-else class="text-center text-muted py-5 border rounded bg-light d-flex flex-column align-items-center justify-content-center">
                <i class="fa fa-map-signs mb-3" style="font-size: 48px; color: #ced4da;"></i>
                <h6 class="font-weight-bold mb-1">No Routes Found</h6>
                <p class="mb-0 small">This module doesn't have any routes configured yet.</p>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { viewModuleDetails } from "@/services/dataService";
import { useToast } from "vue-toastification";

export default {
  data() {
    return {
      token: localStorage.getItem('token_set'),
      loading: false,
      moduleId: null,
      moduleName: '',
      submodules: [],
      showLeftArrow: false,
      showRightArrow: false
    }
  },
  computed: {
    validSubmodules() {
      return this.submodules.filter(s => Number(s.submodule_id) !== 0);
    },
    allRoutes() {
      let routes = [];
      this.submodules.forEach(sub => {
        if (sub.routes && sub.routes.length > 0) {
          sub.routes.forEach(route => {
            routes.push({
              ...route,
              submodule_name: sub.submodule_name
            });
          });
        }
      });
      return routes;
    }
  },
  mounted() {
    this.moduleId = Number(this.$route.params.id) || null;
    if (this.moduleId) {
      this.fetchModuleDetails();
    }
    window.addEventListener('resize', this.checkScroll);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkScroll);
  },
  methods: {
    async fetchModuleDetails() {
      const toast = useToast();
      this.loading = true;
      try {
        const response = await viewModuleDetails(this.token, this.moduleId);
        if (response.data.status === true) {
          const data = response.data.data;
          this.moduleName = data.module_name;
          this.submodules = data.submodules || [];
          this.$nextTick(() => {
            this.checkScroll();
          });
        } else {
          toast.error(response.data.message || "Failed to retrieve module details");
        }
      } catch (error) {
        toast.error(error.response?.data?.message || "An error occurred while fetching details.");
      } finally {
        this.loading = false;
      }
    },
    scrollLeft() {
      if (this.$refs.scrollContainer) {
        this.$refs.scrollContainer.scrollBy({ left: -250, behavior: 'smooth' });
      }
    },
    scrollRight() {
      if (this.$refs.scrollContainer) {
        this.$refs.scrollContainer.scrollBy({ left: 250, behavior: 'smooth' });
      }
    },
    handleScroll() {
      this.checkScroll();
    },
    checkScroll() {
      const container = this.$refs.scrollContainer;
      if (container) {
        this.showLeftArrow = container.scrollLeft > 0;
        this.showRightArrow = Math.ceil(container.scrollLeft + container.clientWidth) < container.scrollWidth;
      }
    }
  }
}
</script>

<style scoped>
.form-group label {
  font-weight: bold;
}
.table-hover tbody tr:hover td {
  background-color: #f1f5f9 !important;
}
.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.hide-scrollbar::-webkit-scrollbar {
  display: none;
}
</style>